import { NotebookFile } from '../../../interfaces/file.interface';
import { FILE_EXTENSIONS } from '../../../constants/general.constants';
import { GeneralHelpers } from '../../../helpers/general.helper';
import { PythonFileSystemHelper } from '../filesystem/python-filesystem.helper';
import { PythonPackageHelper } from '../packages/python-package.helper';

/**
 * PythonFileLoadingHelper
 *
 * Handles sequential loading of files into Pyodide filesystem.
 * Extracted from PythonService to reduce monolithic service size.
 *
 * Responsibilities:
 * - PHASE 1: Install wheel packages (.whl files)
 * - PHASE 2: Load Python modules (.py files)
 * - PHASE 3: Load data files (.csv, .json, etc.)
 */
export class PythonFileLoadingHelper {
  /** Files that failed to load since the last takeFailures(), as "name: reason". */
  #failures: string[] = [];

  constructor(
    private readonly fileSystemHelper: PythonFileSystemHelper,
    private readonly packageHelper: PythonPackageHelper,
    private readonly workingDir: string
  ) {}

  /**
   * The files that failed to load since the last call, then forgets them. A
   * failed write used to vanish into the console, so a step later saw only
   * "No such file" (EHQ-542). The caller reports these on the step. Files left
   * out by the size limits come first (EHQ-548).
   */
  takeFailures(): string[] {
    const failures = [...this.fileSystemHelper.takeSkippedFiles(), ...this.#failures];
    this.#failures = [];
    return failures;
  }

  /** Record a load that didn't succeed, whether it threw or returned errors. */
  #recordFailure(fileName: string, reason: unknown): void {
    const text = Array.isArray(reason) ? reason.join('; ') : GeneralHelpers.errorMessage(reason);
    this.#failures.push(`${fileName}: ${text || 'unknown error'}`);
  }

  /**
   * PHASE 1: Install all packages first (.whl files)
   * Sequential loading phase 1 - ensures dependencies are ready
   */
  async installAllPackages(files: NotebookFile[]): Promise<void> {
    const packageFiles = files.filter(file =>
      GeneralHelpers.fileExtensionFromString(file.name) === FILE_EXTENSIONS.whl
    );

    if (packageFiles.length === 0) {
      return;
    }

    for (const file of packageFiles) {
      try {
        // Skip files without content
        const content = (file as any).content;
        if (!content) {
          continue;
        }

        const metadata = {
          origin: ((file as any).isGlobal ? 'user' : 'project') as 'user' | 'project',
          purpose: 'data' as const,
          persistent: true  // All files persist by default
        };

        // Pass file object for subdirectory routing (typically .whl goes to root)
        const result = await this.fileSystemHelper.installPackageOnly(file.name, content, metadata, file);
        if (result?.success === false) this.#recordFailure(file.name, result.errors);
      } catch (error: any) {
        console.warn('Python file loading: package install failed', { fileName: file.name, error });
        this.#recordFailure(file.name, error);
      }
    }
  }

  /**
   * PHASE 2: Load all Python modules second (.py files)
   *
   * TWO-PASS to support project modules that import sibling project modules
   * (the "1 file = 1 step" / shared-module pattern). A single scan-then-write
   * loop is broken: it import-scans each module BEFORE any module is on
   * sys.path, so `import sibling_module` fails find_spec, is sent to micropip,
   * 404s, throws, and the file is never written — cascading to
   * "No module named ..." at step run.
   *
   *   Pass 1: write EVERY module into /project/ (already on sys.path).
   *   invalidate_caches(): make the just-written files visible to find_spec.
   *   Pass 2: resolve each module's third-party imports — sibling project
   *           modules now resolve via find_spec (skip micropip); genuine PyPI
   *           deps still install.
   *
   * This matches the headless executor (enoch), which mounts all files before
   * resolving imports.
   */
  async loadAllModules(
    files: NotebookFile[],
    processImports: (content: string, filePath: string) => Promise<void>
  ): Promise<void> {
    const moduleFiles = files.filter(file =>
      GeneralHelpers.fileExtensionFromString(file.name) === FILE_EXTENSIONS.py
    );

    if (moduleFiles.length === 0) {
      return;
    }

    // An empty module (an __init__.py) is real content; only a missing one is skipped.
    const withContent = moduleFiles.filter(file => (file as any).content !== undefined && (file as any).content !== null);

    // Pass 1: write every module into the filesystem first, so sibling
    // project-module imports resolve during the import-resolution pass.
    for (const file of withContent) {
      try {
        const content = (file as any).content;
        const metadata = {
          origin: ((file as any).isGlobal ? 'user' : 'project') as 'user' | 'project',
          purpose: 'module' as const,
          persistent: true  // All files persist by default
        };
        const result = await this.fileSystemHelper.loadModuleOnly(file.name, content, metadata, file);
        if (result?.success === false) this.#recordFailure(file.name, result.errors);
      } catch (error: any) {
        console.warn('Python file loading: module write failed', { fileName: file.name, error });
        this.#recordFailure(file.name, error);
      }
    }

    // Make the freshly-written modules visible to importlib.find_spec().
    await this.packageHelper.invalidateImportCaches();

    // Pass 2: resolve each module's imports. Sibling project modules are now on
    // sys.path (skipped by the find_spec guard); only genuine PyPI deps install.
    // Pass empty filePath to disable Journal caching (Module List files can be
    // updated by users, so cached analysis may be stale).
    for (const file of withContent) {
      try {
        await processImports((file as any).content, '');
      } catch (error: any) {
        console.warn('Python file loading: module import processing failed', { fileName: file.name, error });
      }
    }
  }

  /**
   * PHASE 3: Load all data files last (.csv, .json, etc.)
   * Sequential loading phase 3 - everything else
   */
  async loadAllDataFiles(files: NotebookFile[]): Promise<void> {
    const dataFiles = files.filter(file => {
      const ext = GeneralHelpers.fileExtensionFromString(file.name);
      return ext !== FILE_EXTENSIONS.py && ext !== FILE_EXTENSIONS.whl;
    });

    if (dataFiles.length === 0) {
      return;
    }

    for (const file of dataFiles) {
      try {
        // Check if content exists
        const content = (file as any).content;

        // Skip only if both primary and fallback failed. An empty file ('')
        // is real content and must still appear in the filesystem.
        if (content === undefined || content === null) {
          continue;
        }

        const metadata = {
          origin: ((file as any).isGlobal ? 'user' : 'project') as 'user' | 'project',
          purpose: 'data' as const,
          persistent: true  // All files persist by default
        };

        // Pass file object for subdirectory routing (Filesystem Restructure)
        const result = await this.fileSystemHelper.loadDataFileOnly(file.name, content, metadata, file);
        if (result?.success === false) this.#recordFailure(file.name, result.errors);
      } catch (error: any) {
        console.warn('Python file loading: data file load failed', { fileName: file.name, error });
        this.#recordFailure(file.name, error);
      }
    }
  }

  /**
   * Load all files (packages + modules + data)
   */
  async loadAllFiles(
    files: NotebookFile[],
    processImports: (content: string, filePath: string) => Promise<void>
  ): Promise<void> {
    await this.installAllPackages(files);
    await this.loadAllModules(files, processImports);
    await this.loadAllDataFiles(files);
  }

  /**
   * Load project files only (modules + data, skip packages)
   */
  async loadProjectFilesOnly(
    files: NotebookFile[],
    processImports: (content: string, filePath: string) => Promise<void>
  ): Promise<void> {
    // Skip packages (they are already installed), reload modules and data
    await this.loadAllModules(files, processImports);
    await this.loadAllDataFiles(files);
  }

  /**
   * Load workflow files only (data files)
   */
  async loadWorkflowFilesOnly(files: NotebookFile[]): Promise<void> {
    // Skip packages and modules, reload only data files
    await this.loadAllDataFiles(files);
  }
}
